// JOBE-0101: <EntityBrowser> — the extracted host component, the
// default consumer of useEntityBrowserState. Default-renders the
// `<LibraryGrid>` three-column layout (chip rail + list + inspector)
// that the library uses today. Surfaces that need bespoke composition
// consume `useEntityBrowserState` directly instead.
//
// Props (spec §Decision 1 + §Decision 2):
//   - `anchor`:           optional `{ inProject?, ... }` filter merged
//                         into the /api/entities query.
//   - `urlStateNamespace`: optional non-empty, dot-free string. Each
//                         URL key the host owns is prefixed
//                         `<namespace>.`. Empty or dotted → throws.
//   - `roster`:           required when the default <ChipRow> is
//                         rendered (the composer's PeopleValuePopover
//                         reads it). Surfaces that pass a custom rail
//                         AND a custom list-renderer can omit it.
//   - `renderRow`:        optional row renderer override. Not threaded
//                         through `<LibraryListBody>` today (the row
//                         renderer is per-kind via the existing
//                         registry); reserved for the Projects Index
//                         consumer's custom-row use case per spec
//                         §Scope item 1.
//   - `rail`:             optional rail slot. Defaults to none — the
//                         library passes <LibraryRail> from its outer
//                         page. Receivers that want the bare list +
//                         inspector + chip row can omit it.
//   - `inspector`:        optional inspector slot. Defaults to the
//                         per-kind <Inspector> registry. Surfaces that
//                         pass their own can lift selection out via the
//                         hook (see useEntityBrowserState).
//   - `header`:           optional header content (e.g. page title +
//                         sort selector). The library passes its
//                         "Library" + sort dropdown header.
//   - `onSelectionChange`: optional callback `(urn) => void`. Fires
//                         after the host's own selection toggle.
//   - `defaultChips`:     optional array of chip objects applied on
//                         first mount IFF the namespaced URL slice is
//                         empty (no chips, no sort, no cursor, no
//                         view). URL-supplied chips always win. Used by
//                         JOBE-0106 sub-tabs to pin `type=task` /
//                         `type=document` defaults without permanently
//                         baking the chip into the host's anchor.
//   - `defaultSort`:      optional sort string applied on first mount
//                         IFF the namespaced URL slice carries no sort
//                         key. URL-supplied sort always wins. Mirrors
//                         the `defaultChips` semantics — the seed is
//                         idempotent and never re-applied after the
//                         user clears or overrides the sort.
//
// Slot props receive the host's published EntityBrowserContext via the
// `useEntityBrowserContext()` hook — saved-views rails, sort headers,
// omni-palette bridges, etc., compose against the host's state without
// re-instantiating a duplicate URL-state hook (which would cause
// write-races on the URL).
//
// Telemetry parity: the host preserves the same callback surface the
// library uses today (onSelect → setSelectedUrn, onChipAppend →
// setChips merge, onLoadMore → setCursor, retry → query.retry).

import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { LibraryGrid } from '../library/LibraryGrid.jsx';
import { ChipRow } from '../library/ChipRow.jsx';
import { LibraryListBody } from '../library/states/LibraryListBody.jsx';
import { Inspector } from '../library/inspector/Inspector.jsx';
import { useEntityBrowserState } from './useEntityBrowserState.js';
import { EntityBrowserContext } from './EntityBrowserContext.js';
import {
  FRIENDLY_TOKENS, isReplaceChip,
} from '../library/chipVocabulary.js';

// mergeChipInto: shared chip-merge transform. Date-bound chips replace;
// set-valued predicates merge + dedupe. Pulled into the host so omni
// palette / saved-views consumers on the outer surface page can share
// the same canonical merge path by using the host's setChips and the
// chip vocabulary helpers — they reach this via the hook's setChips.
export function mergeChipInto(prev, parsed) {
  const idx = prev.findIndex(
    (c) => c.kind === 'predicate' && c.friendlyToken === parsed.friendlyToken,
  );
  if (idx >= 0) {
    const merged = isReplaceChip(parsed.friendlyToken)
      ? { ...prev[idx], values: [...parsed.values] }
      : {
          ...prev[idx],
          values: Array.from(new Set([...(prev[idx].values || []), ...parsed.values])),
        };
    return prev.map((c, i) => (i === idx ? merged : c));
  }
  return [...prev, {
    kind: 'predicate',
    friendlyToken: parsed.friendlyToken,
    wirePredicate: parsed.wirePredicate,
    values: parsed.values,
  }];
}

export function EntityBrowser({
  anchor,
  urlStateNamespace,
  roster,
  renderRow,
  rail,
  inspector,
  header,
  onSelectionChange,
  defaultChips,
  defaultSort,
  children,
}) {
  // Validate the chip-row composition requirement at mount: the default
  // <ChipRow> renders PeopleValuePopover, which reads `roster`. A
  // missing roster would surface as a null-deref deep inside the
  // composer; throw early with a useful message instead.
  // (Custom render paths that hide the chip row are not yet a thing in
  // this host; when they ship the gate moves alongside.)
  if (!roster) {
    throw new Error(
      'EntityBrowser: `roster` is required when the default chip row is rendered '
        + '(the composer\'s PeopleValuePopover reads `roster.humans` / `.agents`). '
        + 'Surfaces that hide the chip row can omit it.',
    );
  }

  const browser = useEntityBrowserState({ anchor, urlStateNamespace });
  const {
    chips, sort, cursor, view,
    setChips, setSort, setCursor, setView,
    rows, nextCursor, loadState, error, retry,
    selectedUrn, setSelectedUrn, toggleSelectedUrn,
  } = browser;

  // urlSliceIsEmpty — shared "is the namespaced URL slice empty?" check
  // used by both the `defaultChips` and `defaultSort` first-mount seeds.
  // The implicit scope chip (`{ kind: 'scope', ... }`) is always present
  // on a fresh hydrate and must be ignored — only user-applied predicate
  // / field chips count as "non-empty" for default-seeding purposes.
  function urlSliceIsEmpty() {
    const userChips = Array.isArray(chips)
      ? chips.filter((c) => c && c.kind !== 'scope')
      : [];
    return (
      userChips.length === 0
      && !cursor
      && !view
      && (sort === 'lastChangedAt:desc' || !sort)
    );
  }

  // JOBE-0122: apply `defaultSort` on first mount when the namespaced
  // URL slice is empty. Mirrors `defaultChips` semantics: URL-supplied
  // sort wins; idempotent (guarded by a ref so we never re-apply after
  // the user clears or overrides the sort).
  const defaultSortAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultSortAppliedRef.current) return;
    if (!defaultSort) return;
    if (!urlSliceIsEmpty()) {
      defaultSortAppliedRef.current = true;
      return;
    }
    defaultSortAppliedRef.current = true;
    if (defaultSort !== sort) setSort(defaultSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort]);

  // JOBE-0106: apply `defaultChips` on first mount when the URL slice
  // owned by this instance is empty. Idempotent — guarded by a ref so
  // subsequent renders (and URL-back-to-empty trips) never re-apply
  // and trample user-cleared state. The implicit scope chip is ignored
  // by `urlSliceIsEmpty` so a fresh-mount with no user filters still
  // counts as empty.
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (!Array.isArray(defaultChips) || defaultChips.length === 0) return;
    if (!urlSliceIsEmpty()) {
      defaultsAppliedRef.current = true;
      return;
    }
    defaultsAppliedRef.current = true;
    // Merge defaults onto the current chip array so the implicit scope
    // chip (and any other non-predicate chips) survive the seed.
    setChips((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      return [...base, ...defaultChips];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChips]);

  // JOBE-0044.14: track the most recent non-empty page the user has
  // seen. Drop-suggestion (filter-empty) reads it for its delta-count
  // heuristic; unreachable reads it for the cached rows render.
  const [lastNonEmptyPage, setLastNonEmptyPage] = useState(null);
  const [lastNonEmptyPageAt, setLastNonEmptyPageAt] = useState(null);
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const firstUrn = rowCount > 0 ? rows[0].urn : '';
  useEffect(() => {
    if (loadState === 'ready' && rowCount > 0) {
      setLastNonEmptyPage(rows);
      setLastNonEmptyPageAt(Date.now());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState, rowCount, firstUrn]);

  const onSelect = useCallback((urn) => {
    toggleSelectedUrn(urn);
    if (onSelectionChange) onSelectionChange(urn);
  }, [toggleSelectedUrn, onSelectionChange]);

  const onChipAppend = useCallback((parsed) => {
    setChips((prev) => mergeChipInto(prev, parsed));
  }, [setChips]);

  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    setCursor(nextCursor);
  }, [nextCursor, setCursor]);

  const onRemoveChip = useCallback((chip) => {
    if (!chip) return;
    setChips((prev) => prev.filter((c) => c !== chip));
  }, [setChips]);

  // Resolve the selected row from the page cache when possible — the
  // Inspector falls back to a single-entity fetch otherwise.
  const selectedRow = selectedUrn
    ? (Array.isArray(rows) ? rows.find((r) => r.urn === selectedUrn) || null : null)
    : null;

  // Default inspector — the library's per-kind <Inspector> registry.
  const defaultInspector = useMemo(
    () => <Inspector entityUrn={selectedUrn} entity={selectedRow} />,
    [selectedUrn, selectedRow],
  );
  const inspectorNode = inspector ?? defaultInspector;

  // The list column: header (sort/title — caller-supplied) + chip row
  // + LibraryListBody (states machine + load-more).
  const list = (
    <>
      {header}
      <ChipRow
        chips={chips}
        onChange={setChips}
        roster={roster}
      />
      <LibraryListBody
        rows={rows}
        loadState={loadState}
        error={error}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        selectedUrn={selectedUrn}
        onSelect={onSelect}
        onChipAppend={onChipAppend}
        onRetry={retry}
        chips={chips}
        lastNonEmptyPage={lastNonEmptyPage}
        lastNonEmptyPageAt={lastNonEmptyPageAt}
        onRemoveChip={onRemoveChip}
        renderRow={renderRow}
      />
    </>
  );

  // Publish the host's full state via context so slot-rendered
  // consumers (rail, header, custom inspectors) can read and dispatch
  // without spinning up a duplicate URL-state hook (which would
  // write-race the URL).
  const ctxValue = useMemo(() => ({
    ...browser,
    // Convenience: the chip-merge helper used by saved-views,
    // omni-palette, type-rail bridges on the outer page.
    mergeChipInto,
  }), [browser]);

  return (
    <EntityBrowserContext.Provider value={ctxValue}>
      <LibraryGrid rail={rail ?? null} list={list} inspector={inspectorNode} />
      {children}
    </EntityBrowserContext.Provider>
  );
}
